import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Item } from '@radix-ui/react-dropdown-menu';
import Icon from './Icon';

const truncate = `
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Description = styled.span`
  ${truncate}

  color: var(--sharkbait-ooh-la-la);
  font-size: var(--x-small);
  font-weight: var(--normal);
  grid-area: description;
  margin-top: 4px;
`;

const MainText = styled.span`
  ${truncate}

  color: var(--pleather);
  font-weight: var(--normal);
  grid-area: main;

  ${({ $description }) =>
    $description &&
    `
      font-weight: var(--bold);
    `}
`;

const Wrapper = styled.div`
  /* Link style overrides */
  --link-active-color: inherit;
  --link-color: inherit;
  --link-focus-outline-color: transparent;
  --link-font-weight: inherit;
  --link-text-decoration: none;

  border-radius: 6px;
  color: var(--pleather);
  cursor: pointer;
  display: block;
  font-size: var(--regular);
  text-decoration: none;
  padding: 10px 16px;
  width: 100%;

  &:focus,
  &:focus-visible {
    background-color: var(--noreaster);
    color: var(--oh-hi-dark);
    outline: none;

    ${MainText} {
      color: var(--oh-hi-dark);
    }

    ${Icon} {
      color: var(--pleather);
    }
  }

  &[disabled] {
    color: var(--dark-resting);
    cursor: not-allowed;

    &:hover {
      background-color: inherit;
    }

    ${MainText},
    ${Description} {
      color: var(--dark-resting);
    }

    ${Icon} {
      color: var(--myst);
    }
  }
`;

const ContentWrapper = styled.div`
  align-items: center;
  display: grid;
  grid-template:
    'prefix main suffix caret'
    '. description . .'
    / min-content 1fr;
  width: 100%;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${Icon} {
    color: var(--sharkbait-ooh-la-la);
    width: 20px;
  }
`;

const PrefixWrapper = styled(IconWrapper)`
  grid-area: prefix;
  padding-right: 8px;
`;

const SuffixWrapper = styled(IconWrapper)`
  grid-area: suffix;
  padding-left: 8px;
`;

const CaretWrapper = styled(IconWrapper)`
  grid-area: caret;

  ${Icon} {
    height: 16px;
    width: 16px;
  }
`;

const BaseContent = ({
  children,
  description,
  prefix,
  suffix,
  caret,
  ...props
}) => (
  <ContentWrapper {...props}>
    {prefix && <PrefixWrapper>{prefix}</PrefixWrapper>}

    <MainText $description={!!description}>{children}</MainText>

    {suffix && <SuffixWrapper>{suffix}</SuffixWrapper>}
    {caret && (
      <CaretWrapper>
        <Icon icon="angle-right" />
      </CaretWrapper>
    )}
    {description && <Description>{description}</Description>}
  </ContentWrapper>
);

BaseContent.propTypes = {
  children: PropTypes.node,
  description: PropTypes.string,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  caret: PropTypes.bool,
};

export const Content = styled(BaseContent)``;

const BaseListRow = ({
  children,
  description,
  disabled,
  prefix,
  onClick,
  suffix,
  caret,
  ...props
}) => (
  <Item asChild disabled={disabled} onSelect={onClick}>
    <Wrapper disabled={disabled} {...props}>
      <Content
        description={description}
        prefix={prefix}
        suffix={suffix}
        caret={caret}
      >
        {children}
      </Content>
    </Wrapper>
  </Item>
);

BaseListRow.propTypes = {
  caret: PropTypes.bool,
  children: PropTypes.node,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  // Component also inherits all props from @radix-ui/react-dropdown-menu
  // see radix docs: https://www.radix-ui.com/docs/primitives/components/dropdown-menu#item
};

const ListRow = styled(BaseListRow)``;

export default ListRow;
